import HalfEdgeMesh from '@three/common/halfEdgeMesh/HalfEdgeMesh';
import { Vertex } from '@/thirdPartyLibraries/three-mesh-halfedge/src';
import { Vector3 } from 'three';

export type FlattenYMode = 'meshMin' | 'borderMin' | 'borderMax' | number;

export interface FlattenBorderOptions {
  /** Collapse threshold for adjacent boundary edges (world units). Default ≈ 0.2 × mean edge. */
  collapseEps?: number;
  /** Max collapse passes; each pass recomputes the boundary loop. Default 8. */
  maxPasses?: number;
  /** Move target to the midpoint when collapsing. Default true. */
  moveToMidpoint?: boolean;
  /** If true, calls `updateGeometryFromHalfedges()` at the end. Default false. */
  updateGeometry?: boolean;
  /**
   * Optional push direction. If provided, each border vertex is moved along this
   * vector by a scalar t such that (p + t*dir).y equals the target Y. If |dir.y|
   * is nearly zero, the function falls back to setting p.y = targetY without XZ shift.
   */
  pushDir?: Vector3;
}

/**
 * Moves the current boundary vertices to a target Y-plane, then collapses short boundary edges
 * to remove overlaps introduced by the flattening.
 */
export function flattenBorderToY(
  mesh: HalfEdgeMesh,
  mode: FlattenYMode,
  opts: FlattenBorderOptions = {},
): void {
  const ds = mesh.halfEdges;
  const meanEdge = Math.max(ds.getMeanEdgeLength?.() ?? 0.05, 1e-4);
  const collapseEps = opts.collapseEps ?? 0.1 * meanEdge;
  const maxPasses = Math.max(1, opts.maxPasses ?? 8);
  const moveToMidpoint = opts.moveToMidpoint ?? true;

  const targetY = (() => {
    if (typeof mode === 'number') return mode;
    if (mode === 'borderMin') {
      const border = mesh.getBorderVertices();
      if (border.length > 0) return Math.min(...border.map((v) => v.position.y));
    }
    if (mode === 'borderMax') {
      const border = mesh.getBorderVertices();
      if (border.length > 0) return Math.max(...border.map((v) => v.position.y));
    }
    // meshMin fallback
    let minY = Number.POSITIVE_INFINITY;
    for (const v of ds.vertices) if (v.position.y < minY) minY = v.position.y;
    return isFinite(minY) ? minY : 0;
  })();

  // 1) Move current boundary vertices onto the Y plane
  const borderNow = mesh.getBorderVertices();
  const dir = opts.pushDir;
  for (const v of borderNow) {
    if (dir && Math.abs(dir.y) > 1e-12) {
      // Move along the provided direction until Y hits targetY
      const t = (targetY - v.position.y) / dir.y;
      v.position.addScaledVector(dir, t);
      // Numerical guard
      v.position.y = targetY;
    } else {
      // No direction provided or direction has no Y component → set Y directly
      v.position.y = targetY;
    }
  }

  // 2) Collapse adjacent boundary edges that are below the threshold
  for (let pass = 0; pass < maxPasses; pass++) {
    const loop: Vertex[] = mesh.getConnectedBorderVertices();
    const L = loop.length;
    if (L < 3) break;
    let collapsed = 0;

    const used = new Set<number>();
    for (let i = 0; i < L; i++) {
      const a = loop[i];
      const b = loop[(i + 1) % L];
      if (used.has(a.id) || used.has(b.id)) continue;
      const dist = a.position.distanceTo(b.position);
      if (dist < collapseEps) {
        // Try both directions if needed (boundary may prefer one orientation)
        if (ds.edgeCollapse(a, b, moveToMidpoint) || ds.edgeCollapse(b, a, moveToMidpoint)) {
          used.add(a.id);
          used.add(b.id);
          collapsed++;
        }
      }
    }

    if (collapsed === 0) break;
    // Recompute border vertices set for the next pass
    mesh.calculateBorderVertices();
  }

  if (opts.updateGeometry) {
    mesh.updateGeometryFromHalfedges();
  } else {
    mesh.calculateBorderVertices();
  }
}
